import { Link, useParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { useFavorite } from "@/hooks/useFavorite";
import {
  getDramaById,
  getActorsByIds,
  getReviewsByDrama,
  getRelatedDramas,
  getRatingAverage,
} from "@/lib/content";
import DetailHeader from "./components/DetailHeader";
import DramaCard from "@/components/feature/DramaCard";
import Stars from "@/components/base/Stars";

const sections = [
  { id: "intro", label: "剧情简介", icon: "ri-book-open-line" },
  { id: "chapters", label: "分集列表", icon: "ri-list-ordered" },
  { id: "cast", label: "主要演员", icon: "ri-user-star-line" },
  { id: "reviews", label: "剧集点评", icon: "ri-chat-quote-line" },
  { id: "related", label: "相关推荐", icon: "ri-movie-2-line" },
];

export default function Detail() {
  const { id } = useParams();
  const drama = getDramaById(id);
  const { fav, toggle } = useFavorite(id || "");

  useSeo({
    title: drama
      ? `风流女管家 - ${drama.title}剧情详情及演员介绍`
      : "风流女管家 - 剧集详情",
    description: drama
      ? `${drama.title}（${drama.year}年${drama.genre}）剧情简介、分集解析与主演资料，由${drama.director}执导，${drama.summary}`
      : "风流女管家剧集详情页，提供剧情简介、分集列表与演员介绍。",
    keywords: drama
      ? `${drama.title},${drama.title}剧情,${drama.title}演员表,风流女管家`
      : "风流女管家,剧集详情,剧情介绍",
  });

  const scrollTo = (sectionId: string) => {
    document
      .getElementById(sectionId)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (!drama) {
    return (
      <div className="mx-auto flex w-full max-w-6xl flex-col items-center justify-center px-4 py-24 text-center md:px-6">
        <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-400">
          <i className="ri-error-warning-line text-3xl" />
        </span>
        <h1 className="font-heading text-2xl font-bold text-foreground-950">
          该剧集不存在或已下架
        </h1>
        <p className="mt-2 text-sm text-foreground-500">
          你访问的剧集可能已被移除，看看其他精彩内容吧。
        </p>
        <Link
          to="/category"
          className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-arrow-left-line" />
          返回剧集分类
        </Link>
      </div>
    );
  }

  const cast = getActorsByIds(drama.castIds);
  const dramaReviews = getReviewsByDrama(drama.id);
  const avgRating = getRatingAverage(dramaReviews);
  const related = getRelatedDramas(drama);

  return (
    <div className="animate-fade-in">
      <DetailHeader drama={drama} isFav={fav} onToggleFav={toggle} />

      <div className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_240px]">
          {/* Main content */}
          <div className="min-w-0">
            {/* Intro */}
            <section id="intro" className="scroll-mt-24">
              <h2 className="mb-4 flex items-center gap-2 font-heading text-2xl font-bold text-foreground-950">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className="ri-book-open-line" />
                </span>
                剧情简介
              </h2>
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <dl className="mb-5 grid grid-cols-1 gap-x-8 gap-y-2.5 border-b border-background-200 pb-5 text-sm sm:grid-cols-2">
                  <div className="flex gap-2">
                    <dt className="shrink-0 text-foreground-500">导演：</dt>
                    <dd className="text-foreground-900">{drama.director}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="shrink-0 text-foreground-500">编剧：</dt>
                    <dd className="text-foreground-900">{drama.writer}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="shrink-0 text-foreground-500">主演：</dt>
                    <dd className="text-foreground-900">
                      {cast.map((a) => a.name).join("、")}
                    </dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="shrink-0 text-foreground-500">上映时间：</dt>
                    <dd className="text-foreground-900">{drama.updateTime}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="shrink-0 text-foreground-500">集数：</dt>
                    <dd className="text-foreground-900">全 {drama.episodes} 集</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="shrink-0 text-foreground-500">地区 / 类型：</dt>
                    <dd className="text-foreground-900">
                      {drama.region} · {drama.genre}
                    </dd>
                  </div>
                </dl>
                <div className="space-y-4 text-sm leading-7 text-foreground-700">
                  {drama.fullSynopsis.split("\n").map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>
              </div>
            </section>

            {/* Chapters */}
            <section id="chapters" className="mt-12 scroll-mt-24">
              <h2 className="mb-4 flex items-center gap-2 font-heading text-2xl font-bold text-foreground-950">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className="ri-list-ordered" />
                </span>
                分集列表
                <span className="ml-1 text-sm font-normal text-foreground-500">
                  （共 {drama.chapters.length} 条精选）
                </span>
              </h2>
              <div className="divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
                {drama.chapters.map((chapter) => (
                  <Link
                    key={chapter.ep}
                    to={`/reviews/${drama.id}?ep=${chapter.ep}`}
                    className="group flex cursor-pointer items-start gap-4 p-4 transition-colors hover:bg-background-100"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-100 font-heading text-sm font-semibold text-accent-800">
                      {chapter.ep}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <span className="text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-600">
                          第 {chapter.ep} 集 · {chapter.title}
                        </span>
                        <span className="text-xs text-foreground-400">
                          {chapter.date}
                        </span>
                      </span>
                      <span className="mt-1 block text-sm leading-relaxed text-foreground-500">
                        {chapter.synopsis}
                      </span>
                    </span>
                    <i className="ri-arrow-right-s-line mt-2 text-foreground-300 transition-colors group-hover:text-primary-500" />
                  </Link>
                ))}
              </div>
            </section>

            {/* Cast */}
            <section id="cast" className="mt-12 scroll-mt-24">
              <h2 className="mb-4 flex items-center gap-2 font-heading text-2xl font-bold text-foreground-950">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className="ri-user-star-line" />
                </span>
                主要演员
              </h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {cast.map((actor) => (
                  <Link
                    key={actor.id}
                    to={`/actor/${actor.id}`}
                    className="group flex cursor-pointer gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-200"
                  >
                    <span className="block h-20 w-16 shrink-0 overflow-hidden rounded-md bg-background-200">
                      <img
                        src={actor.avatar}
                        alt={`${actor.name}头像`}
                        title={`${actor.name} 演员头像`}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-base font-semibold text-foreground-900 transition-colors group-hover:text-primary-600">
                        {actor.name}
                      </span>
                      <span className="mt-0.5 block text-sm text-primary-600">
                        饰 {actor.roleInDrama[drama.id] || actor.signatureRoles[0]}
                      </span>
                      <span className="mt-1.5 line-clamp-2 block text-xs leading-relaxed text-foreground-500">
                        {actor.intro}
                      </span>
                    </span>
                  </Link>
                ))}
              </div>
            </section>

            {/* Reviews */}
            <section id="reviews" className="mt-12 scroll-mt-24">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <h2 className="flex items-center gap-2 font-heading text-2xl font-bold text-foreground-950">
                  <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className="ri-chat-quote-line" />
                  </span>
                  剧集点评精选
                </h2>
                <Link
                  to={`/reviews/${drama.id}`}
                  className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                >
                  查看更多点评
                  <i className="ri-arrow-right-s-line" />
                </Link>
              </div>

              {dramaReviews.length > 0 ? (
                <>
                  <div className="mb-4 flex items-center gap-4 rounded-lg bg-background-100 p-4">
                    <span className="font-heading text-4xl font-bold text-primary-600">
                      {avgRating}
                    </span>
                    <div>
                      <Stars value={avgRating} className="text-base" />
                      <p className="mt-1 text-xs text-foreground-500">
                        基于 {dramaReviews.length} 条观众点评
                      </p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {dramaReviews.slice(0, 3).map((review) => (
                      <div
                        key={review.id}
                        className="rounded-lg border border-background-200 bg-background-50 p-4"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-xs font-medium text-primary-700">
                              {review.user.slice(0, 1)}
                            </span>
                            <span className="text-sm font-medium text-foreground-900">
                              {review.user}
                            </span>
                          </div>
                          <Stars value={review.rating} className="text-sm" />
                        </div>
                        <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                          {review.content}
                        </p>
                        <p className="mt-2 text-xs text-foreground-400">
                          {review.date} · {review.likes} 人觉得有用
                        </p>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-10 text-center">
                  <p className="text-sm text-foreground-500">
                    暂无点评，欢迎前往点评页留下你的看法
                  </p>
                  <Link
                    to={`/reviews/${drama.id}`}
                    className="mt-3 inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm text-background-50 transition-colors hover:bg-primary-600"
                  >
                    去写点评
                  </Link>
                </div>
              )}
            </section>

            {/* Related */}
            {related.length > 0 && (
              <section id="related" className="mt-12 scroll-mt-24">
                <h2 className="mb-4 flex items-center gap-2 font-heading text-2xl font-bold text-foreground-950">
                  <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className="ri-movie-2-line" />
                  </span>
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                  {related.map((item) => (
                    <DramaCard key={item.id} drama={item} />
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Sticky rail */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 space-y-4">
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <p className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-foreground-900">
                  <i className="ri-list-check-2 text-primary-600" />
                  本页目录
                </p>
                <ul className="space-y-1">
                  {sections.map((sec) => (
                    <li key={sec.id}>
                      <button
                        type="button"
                        onClick={() => scrollTo(sec.id)}
                        className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-foreground-600 transition-colors hover:bg-background-100 hover:text-primary-600"
                      >
                        <i className={`${sec.icon} text-base text-foreground-400`} />
                        {sec.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <p className="mb-3 text-sm font-semibold text-foreground-900">
                  主要演员快跳
                </p>
                <ul className="space-y-2">
                  {cast.map((actor) => (
                    <li key={actor.id}>
                      <Link
                        to={`/actor/${actor.id}`}
                        className="flex cursor-pointer items-center gap-2 text-sm text-foreground-600 transition-colors hover:text-primary-600"
                      >
                        <span className="block h-8 w-8 shrink-0 overflow-hidden rounded-full bg-background-200">
                          <img
                            src={actor.avatar}
                            alt={actor.name}
                            title={`${actor.name} 演员头像`}
                            className="h-full w-full object-cover object-top"
                          />
                        </span>
                        {actor.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <button
                type="button"
                onClick={toggle}
                className={`flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-3 text-sm font-medium transition-colors ${
                  fav
                    ? "bg-accent-500 text-foreground-950"
                    : "bg-primary-500 text-background-50 hover:bg-primary-600"
                }`}
              >
                <i className={fav ? "ri-bookmark-fill" : "ri-bookmark-line"} />
                {fav ? "已加入收藏" : "收藏这部剧"}
              </button>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}